import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, Users, Calendar, DollarSign, RefreshCw, BarChart3, Download, Code } from 'lucide-react';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import WidgetAnalytics from './WidgetAnalytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#14b8a6', '#06b6d4', '#8b5cf6', '#f59e0b', '#ef4444', '#10b981'];

export default function AnalyticsTab() {
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [revenueData, setRevenueData] = useState(null);
  const [memberData, setMemberData] = useState(null);
  const [appointmentData, setAppointmentData] = useState(null);
  const [kpiData, setKpiData] = useState(null);

  useEffect(() => {
    loadAnalytics();
  }, [dateRange, customStartDate, customEndDate]);

  const getDateParams = () => {
    const now = new Date();
    
    // If custom date range is selected and both dates are provided
    if (dateRange === 'custom' && customStartDate && customEndDate) {
      return {
        start_date: format(new Date(customStartDate), 'yyyy-MM-dd\'T\'00:00:00\'Z\''),
        end_date: format(new Date(customEndDate), 'yyyy-MM-dd\'T\'23:59:59\'Z\'')
      };
    }
    
    switch (dateRange) {
      case 'month':
        return {
          start_date: format(startOfMonth(now), 'yyyy-MM-dd\'T\'HH:mm:ss\'Z\''),
          end_date: format(endOfMonth(now), 'yyyy-MM-dd\'T\'HH:mm:ss\'Z\'')
        };
      case 'quarter':
        return {
          start_date: format(startOfMonth(subMonths(now, 3)), 'yyyy-MM-dd\'T\'HH:mm:ss\'Z\''),
          end_date: format(endOfMonth(now), 'yyyy-MM-dd\'T\'HH:mm:ss\'Z\'')
        };
      case 'year':
        return {
          start_date: format(startOfMonth(subMonths(now, 12)), 'yyyy-MM-dd\'T\'HH:mm:ss\'Z\''),
          end_date: format(endOfMonth(now), 'yyyy-MM-dd\'T\'HH:mm:ss\'Z\'')
        };
      default:
        return {};
    }
  };

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = getDateParams();
      const queryString = new URLSearchParams(params).toString();

      const [revenue, members, appointments, kpis] = await Promise.all([
        axios.get(`${API_URL}/api/analytics/revenue?${queryString}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/analytics/members?${queryString}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/analytics/appointments?${queryString}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/analytics/kpis`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setRevenueData(revenue.data);
      setMemberData(members.data);
      setAppointmentData(appointments.data);
      setKpiData(kpis.data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = (data, filename) => {
    if (!data || data.length === 0) {
      alert('No data to export');
      return;
    }

    // Convert data to CSV
    const headers = Object.keys(data[0]);
    const csvRows = [headers.join(',')];
    
    for (const row of data) {
      const values = headers.map(header => {
        const val = row[header];
        return typeof val === 'string' ? `"${val}"` : val;
      });
      csvRows.push(values.join(','));
    }
    
    const csvString = csvRows.join('\n');
    
    // Download CSV
    const blob = new Blob([csvString], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('hidden', '');
    a.setAttribute('href', url);
    a.setAttribute('download', `${filename}_${format(new Date(), 'yyyy-MM-dd')}.csv`);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const exportRevenueData = () => {
    const data = [
      { metric: 'Total Revenue', value: revenueData.total_revenue },
      { metric: 'Total Transactions', value: revenueData.total_transactions },
      { metric: 'Average Transaction', value: revenueData.average_transaction },
      ...revenueData.revenue_trend.map(item => ({
        month: item.month,
        revenue: item.revenue
      }))
    ];
    exportToCSV(data, 'revenue_analytics');
  };

  const exportMemberData = () => {
    const data = [
      { metric: 'Total Members', value: memberData.total_members },
      { metric: 'Active Members', value: memberData.active_members },
      { metric: 'Inactive Members', value: memberData.inactive_members },
      { metric: 'Active Percentage', value: memberData.active_percentage },
      ...memberData.growth_trend.map(item => ({
        month: item.month,
        new_members: item.count
      }))
    ];
    exportToCSV(data, 'member_analytics');
  };

  const exportAppointmentData = () => {
    const data = [
      { metric: 'Total Appointments', value: appointmentData.total_appointments },
      { metric: 'Scheduled', value: appointmentData.scheduled },
      { metric: 'Completed', value: appointmentData.completed },
      { metric: 'Cancelled', value: appointmentData.cancelled },
      { metric: 'Completion Rate', value: appointmentData.completion_rate },
      ...appointmentData.appointments_trend.map(item => ({
        month: item.month,
        appointments: item.count
      }))
    ];
    exportToCSV(data, 'appointment_analytics');
  };

  if (loading) {
    return <div className="p-6 text-center">Loading analytics...</div>;
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Analytics Dashboard</h1>
          <p className="text-gray-500 mt-1">Comprehensive insights into your clinic's performance</p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <select
            value={dateRange}
            onChange={(e) => {
              setDateRange(e.target.value);
              if (e.target.value !== 'custom') {
                setCustomStartDate('');
                setCustomEndDate('');
              }
            }}
            className="px-4 py-2 border rounded-lg bg-white"
          >
            <option value="all">All Time</option>
            <option value="month">This Month</option>
            <option value="quarter">Last 3 Months</option>
            <option value="year">Last 12 Months</option>
            <option value="custom">Custom Range</option>
          </select>
          
          {dateRange === 'custom' && (
            <>
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="px-3 py-2 border rounded-lg bg-white text-sm"
                placeholder="Start Date"
              />
              <span className="text-gray-500">to</span>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="px-3 py-2 border rounded-lg bg-white text-sm"
                placeholder="End Date"
                min={customStartDate}
              />
            </>
          )}
          
          <Button onClick={loadAnalytics} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Monthly Recurring Revenue</CardDescription>
            <CardTitle className="text-3xl">${kpiData?.mrr?.toFixed(2) || 0}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center text-sm text-green-600">
              <TrendingUp className="h-4 w-4 mr-1" />
              <span>MRR from active memberships</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Monthly Revenue</CardDescription>
            <CardTitle className="text-3xl">${revenueData?.total_revenue?.toFixed(2) || 0}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center text-sm text-gray-600">
              <DollarSign className="h-4 w-4 mr-1" />
              <span>{revenueData?.total_transactions || 0} transactions</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Active Members</CardDescription>
            <CardTitle className="text-3xl">{memberData?.active_members || 0}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center text-sm text-gray-600">
              <Users className="h-4 w-4 mr-1" />
              <span>{memberData?.active_percentage || 0}% of total members</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Avg Customer LTV</CardDescription>
            <CardTitle className="text-3xl">${kpiData?.average_ltv?.toFixed(2) || 0}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center text-sm text-gray-600">
              <TrendingUp className="h-4 w-4 mr-1" />
              <span>Lifetime value per member</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for different analytics */}
      <Tabs defaultValue="revenue" className="space-y-4">
        <TabsList>
          <TabsTrigger value="revenue">Revenue</TabsTrigger>
          <TabsTrigger value="members">Members</TabsTrigger>
          <TabsTrigger value="appointments">Appointments</TabsTrigger>
          <TabsTrigger value="widget" className="flex items-center gap-1">
            <Code className="h-4 w-4" />
            Widget
          </TabsTrigger>
        </TabsList>

        {/* Revenue Tab */}
        <TabsContent value="revenue" className="space-y-4">
          <div className="flex justify-end mb-4">
            <Button onClick={exportRevenueData} variant="outline">
              <Download className="h-4 w-4 mr-2" />
              Export to CSV
            </Button>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Revenue Trend */}
            <Card className="col-span-1 lg:col-span-2">
              <CardHeader>
                <CardTitle>Revenue Trend</CardTitle>
                <CardDescription>Monthly revenue over time</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={revenueData?.revenue_trend || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip formatter={(value) => `$${value}`} />
                    <Legend />
                    <Line type="monotone" dataKey="revenue" stroke="#14b8a6" strokeWidth={2} name="Revenue" />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Revenue by Method */}
            <Card>
              <CardHeader>
                <CardTitle>Revenue by Payment Method</CardTitle>
                <CardDescription>Distribution across payment types</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={Object.entries(revenueData?.revenue_by_method || {}).map(([key, value]) => ({
                        name: key,
                        value: value
                      }))}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {Object.keys(revenueData?.revenue_by_method || {}).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => `$${value}`} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Revenue by Type */}
            <Card>
              <CardHeader>
                <CardTitle>Revenue by Type</CardTitle>
                <CardDescription>Breakdown by revenue category</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={Object.entries(revenueData?.revenue_by_type || {}).map(([key, value]) => ({
                    type: key,
                    revenue: value
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="type" />
                    <YAxis />
                    <Tooltip formatter={(value) => `$${value}`} />
                    <Bar dataKey="revenue" fill="#14b8a6" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Stats Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Total Transactions</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold">{revenueData?.total_transactions || 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Average Transaction</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold">${revenueData?.average_transaction?.toFixed(2) || 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Total Revenue</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-teal-600">${revenueData?.total_revenue?.toFixed(2) || 0}</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Members Tab */}
        <TabsContent value="members" className="space-y-4">
          <div className="flex justify-end mb-4">
            <Button onClick={exportMemberData} variant="outline">
              <Download className="h-4 w-4 mr-2" />
              Export to CSV
            </Button>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Member Growth */}
            <Card className="col-span-1 lg:col-span-2">
              <CardHeader>
                <CardTitle>Member Growth</CardTitle>
                <CardDescription>New members over time</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={memberData?.growth_trend || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="count" stroke="#06b6d4" strokeWidth={2} name="New Members" />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Plan Distribution */}
            <Card>
              <CardHeader>
                <CardTitle>Plan Distribution</CardTitle>
                <CardDescription>Members by membership plan</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={memberData?.plan_distribution || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="plan" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="#8b5cf6" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Member Status */}
            <Card>
              <CardHeader>
                <CardTitle>Member Status</CardTitle>
                <CardDescription>Active vs Inactive</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Active', value: memberData?.active_members || 0 },
                        { name: 'Inactive', value: memberData?.inactive_members || 0 }
                      ]}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      <Cell fill="#10b981" />
                      <Cell fill="#ef4444" />
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Member Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Total Members</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold">{memberData?.total_members || 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Active Members</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-green-600">{memberData?.active_members || 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Inactive Members</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-red-600">{memberData?.inactive_members || 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Active Rate</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-teal-600">{memberData?.active_percentage || 0}%</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Appointments Tab */}
        <TabsContent value="appointments" className="space-y-4">
          <div className="flex justify-end mb-4">
            <Button onClick={exportAppointmentData} variant="outline">
              <Download className="h-4 w-4 mr-2" />
              Export to CSV
            </Button>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Appointment Trend */}
            <Card className="col-span-1 lg:col-span-2">
              <CardHeader>
                <CardTitle>Appointment Trend</CardTitle>
                <CardDescription>Appointments over time</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={appointmentData?.appointments_trend || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="count" stroke="#f59e0b" strokeWidth={2} name="Appointments" />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Appointments by Type */}
            <Card>
              <CardHeader>
                <CardTitle>Appointments by Type</CardTitle>
                <CardDescription>Distribution by appointment category</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={appointmentData?.appointments_by_type || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="type" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="#f59e0b" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Appointment Status */}
            <Card>
              <CardHeader>
                <CardTitle>Appointment Status</CardTitle>
                <CardDescription>By completion status</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Scheduled', value: appointmentData?.scheduled || 0 },
                        { name: 'Completed', value: appointmentData?.completed || 0 },
                        { name: 'Cancelled', value: appointmentData?.cancelled || 0 }
                      ]}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      <Cell fill="#06b6d4" />
                      <Cell fill="#10b981" />
                      <Cell fill="#ef4444" />
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Appointment Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Total Appointments</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold">{appointmentData?.total_appointments || 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Completion Rate</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-green-600">{appointmentData?.completion_rate || 0}%</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Cancellation Rate</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-red-600">{appointmentData?.cancellation_rate || 0}%</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Scheduled</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-blue-600">{appointmentData?.scheduled || 0}</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Widget Analytics Tab */}
        <TabsContent value="widget">
          <WidgetAnalytics />
        </TabsContent>
      </Tabs>
    </div>
  );
}
